import { SHContainer } from "@components/design-systems";
import { SelectedSubProductDTO } from "@models/managed-accounts/entities/step/fee";
import { AnalysisVirtualizedStepProps } from "@models/platform-analysis/entities/analysis";
import { debounce, isEqual, isNil } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  sortBySubProductOrder,
  sortFeatureAnalysisRowsByProductOrder,
} from "../../utils";
import { AnalysisTableContainer } from "./components/table-components/container";
import { TableVirtualizationConfigs } from "./configs";

type AnalysisVirtualizedTableProps = {
  stepNumber: number;
  virtualizedProps: AnalysisVirtualizedStepProps;
  disabled?: boolean;
};

export const AnalysisVirtualizedTable = ({
  stepNumber,
  virtualizedProps,
  disabled = false,
}: AnalysisVirtualizedTableProps) => {
  const {
    matchParentContainerWidth,
    containerHeight,
    getSelectedProductsFunc,
    getSelectedSubProductsFunc,
    getCurrentDataFunc,
    getProductRecommendationDataFunc = () => undefined,
  } = virtualizedProps;

  const selectedProducts = useSelector(getSelectedProductsFunc);
  const selectedSubProducts = useSelector(getSelectedSubProductsFunc);
  const currentTableRows = useSelector(getCurrentDataFunc);
  const productRecommendationData = useSelector(
    getProductRecommendationDataFunc,
  );

  // #region Effects & Values
  const [productIdsOrder, setProductIdsOrder] = useState<string[]>(
    Object.keys(productRecommendationData || {}),
  );

  const shouldAlignCenter = useMemo(() => {
    if ((!selectedProducts && !selectedSubProducts) || !currentTableRows)
      return false;

    const numOfItems = isNil(selectedProducts)
      ? selectedSubProducts?.length ?? 0
      : selectedProducts.length;

    return numOfItems > TableVirtualizationConfigs.NumOfItemsToCenter;
  }, [selectedProducts, selectedSubProducts, currentTableRows]);
  // #endregion Effects & Values

  const [orderedSubProducts, setOrderedSubProducts] = useState<
    SelectedSubProductDTO[] | undefined
  >(selectedSubProducts);

  const debounceUpdateOrderedSubProducts = debounce(() => {
    if (!productRecommendationData || !selectedSubProducts) return;

    const sortedData = sortBySubProductOrder({
      data: selectedSubProducts,
      getProductId: (item) => item.productId,
      productRecommendations: productRecommendationData,
    });

    const newIds = sortedData.map((item) => item.id);
    if (!isEqual(productIdsOrder, newIds)) setProductIdsOrder(newIds);

    setOrderedSubProducts(sortedData);
  }, 300);

  const orderedRows = useMemo(() => {
    if (!productRecommendationData || !currentTableRows) {
      return currentTableRows;
    }

    return sortFeatureAnalysisRowsByProductOrder({
      originalRows: currentTableRows,
      productRecommendations: productRecommendationData,
    });
  }, [currentTableRows, productRecommendationData]);

  useEffect(() => {
    debounceUpdateOrderedSubProducts();

    return () => {
      debounceUpdateOrderedSubProducts.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productRecommendationData, selectedSubProducts]);

  return (
    <SHContainer
      maxWidth={shouldAlignCenter ? false : "lg2"}
      sx={{
        px: { xs: "16px", lg2: 0 },
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        "::-webkit-scrollbar": { boxShadow: "none" },
        "::-webkit-scrollbar-thumb": { boxShadow: "none" },
        maxHeight: containerHeight ?? "calc(100vh - 150px)",
        maxWidth: "95vw",
        minWidth: matchParentContainerWidth
          ? "100%"
          : { lg: "1200px", xs: "100%" },
        alignItems: shouldAlignCenter ? "center" : "flex-start",
      }}
    >
      <AnalysisTableContainer
        disabled={disabled}
        stepNumber={stepNumber}
        virtualizedProps={virtualizedProps}
        selectedProducts={selectedProducts}
        selectedSubProducts={orderedSubProducts}
        currentTableRows={orderedRows}
        shouldAlignCenter={shouldAlignCenter}
      />
    </SHContainer>
  );
};
